import { saveClinicalResource, readClinicalResourceList, assertClinicalChartReady, clinicalNetworkRouteId } from './clinicalMutationAdapter';
import { requireClinicalSaveResult } from '../clinicalSaveResult';
import config from '../../config';
import { getAuthHeaders, getAuthHeaderOnly, handleResponse, handleNoContentResponse } from './common';
import { jwtDecode } from 'jwt-decode';
import { formatTimeSpanForSubmit } from '../dateUtils';
import { normalizeAppointmentUtc } from '../appointmentUtc';

export const DUPLICATE_MRN_MESSAGE = 'That MRN is already used by another client in this practice.';

export const getClientApiErrorMessage = (error) => {
    const message = error?.message || String(error || '');
    return message.includes('Client MRN already exists in this organization')
        ? DUPLICATE_MRN_MESSAGE
        : message;
};

const PROFILE_IMAGE_UPLOAD_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const PROFILE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
/** Multipart field name — matches User profile-image and vela-api PR #245 client chart upload. */
const PROFILE_IMAGE_FORM_FIELD = 'file';

const validateProfileImageUploadFile = (imageFile) => {
    if (!imageFile) throw new Error('Image file is required');
    if (!PROFILE_IMAGE_UPLOAD_TYPES.includes(imageFile.type)) {
        throw new Error('Invalid file type. Only JPG and PNG images are allowed.');
    }
    if (imageFile.size > PROFILE_IMAGE_MAX_BYTES) {
        throw new Error('File size exceeds 5MB limit.');
    }
};

const profileImageUploadErrorMessage = (status) => {
    if (status === 413) return 'File size exceeds 5MB limit.';
    if (status === 415) return 'Invalid file type. Only JPG and PNG images are allowed.';
    if (status >= 500) return 'Could not save the profile photo. Please try again.';
    return 'Could not save the profile photo. Please try again.';
};

const parseProfileImageUploadResponse = async (res) => {
    if (res.status === 204) {
        return { success: true };
    }

    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        return res.json();
    }

    const text = await res.text();
    if (text) {
        try {
            return JSON.parse(text);
        } catch {
            return { profileImageUrl: text.trim(), success: true };
        }
    }

    return { success: true };
};

const postProfileImageMultipart = async (url, imageFile) => {
    validateProfileImageUploadFile(imageFile);
    const formData = new FormData();
    formData.append(PROFILE_IMAGE_FORM_FIELD, imageFile);
    return fetch(url, {
        method: 'POST',
        headers: getAuthHeaderOnly(),
        body: formData,
    });
};

const clientApi = {
    fetchClient: async (referenceId) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    fetchClientByUserId: async (userId) => {
        const res = await fetch(`${config.api.url}/clients/by-user/${userId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    updateClient: async (referenceId, clientData) => {
        try {
            if (!referenceId) throw new Error('Reference ID is required');
            if (!clientData) throw new Error('Client data is required');
            const {
                user,
                midwife,
                babies,
                chartEntries,
                pregnancies,
                intakes,
                clientType,
                ...clientFields
            } = clientData;
            const payload = {
                referenceId: clientData.referenceId || referenceId,
                ...clientFields
            };
            if (typeof payload.clientMRN === 'string') {
                payload.clientMRN = payload.clientMRN.trim();
            }
            // Optional date fields: backend expects null for empty, not "" (DateTime? conversion fails on "")
            const optionalDateFields = ['partnerDateOfBirth'];
            for (const field of optionalDateFields) {
                if (payload[field] === '' || payload[field] === undefined) {
                    payload[field] = null;
                }
            }
            // Backend expects bool for these; normalize "yes"/"no" strings
            if (payload.mailingList !== undefined && typeof payload.mailingList !== 'boolean') {
                payload.mailingList = payload.mailingList === 'yes' || payload.mailingList === true;
            }
            if (payload.textCampaignList !== undefined && typeof payload.textCampaignList !== 'boolean') {
                payload.textCampaignList = payload.textCampaignList === 'yes' || payload.textCampaignList === true;
            }

            const res = await fetch(`${config.api.url}/clients/${referenceId}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const errorData = await res.text();
                console.error('Client update failed:', {
                    status: res.status,
                    statusText: res.statusText,
                    error: errorData
                });
                throw new Error(errorData || `Failed to update client. Status: ${res.status}`);
            }

            if (res.status === 204) {
                return payload;
            }

            const responseData = await res.json();
            return responseData;
        } catch (err) {
            console.error('Error in updateClient:', err);
            throw err;
        }
    },

    updateClientPortalLoginEmail: async (referenceId, email, syncChartEmail = true) => {
        if (!referenceId) throw new Error('Reference ID is required');
        if (!email?.trim()) throw new Error('Email is required');

        const res = await fetch(`${config.api.url}/clients/${referenceId}/portal-login-email`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ email: email.trim(), syncChartEmail })
        });
        return handleResponse(res);
    },

    deleteTestClient: async (referenceId, confirmation) => {
        if (!referenceId) throw new Error('Reference ID is required');
        const res = await fetch(
            `${config.api.url}/clients/${referenceId}?confirmation=${encodeURIComponent(confirmation || '')}`,
            { method: 'DELETE', headers: getAuthHeaders() }
        );
        return handleNoContentResponse(res);
    },

    fetchClientMergeCandidates: async (targetReferenceId) => {
        const res = await fetch(`${config.api.url}/clients/${targetReferenceId}/merge-candidates`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    previewClientMerge: async (targetReferenceId, sourceReferenceId) => {
        const res = await fetch(
            `${config.api.url}/clients/${targetReferenceId}/merge-preview/${sourceReferenceId}`,
            { headers: getAuthHeaders() }
        );
        return handleResponse(res);
    },

    mergeClient: async (targetReferenceId, sourceClientReferenceId, confirmation) => {
        const res = await fetch(`${config.api.url}/clients/${targetReferenceId}/merge`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ sourceClientReferenceId, confirmation })
        });
        return handleResponse(res);
    },

    changeAssignedMidwife: async (referenceId, assignedMidwifeReferenceId, clientData = {}) => {
        if (!referenceId) throw new Error('Reference ID is required');
        if (!assignedMidwifeReferenceId) throw new Error('Assigned midwife is required');
        return clientApi.updateClient(referenceId, {
            ...clientData,
            assignedMidwifeReferenceId
        });
    },

    /**
     * @param {string} midwifeId
     * @param {Object} clientData
     * @param {string|null} [clientData.email] Contact email; does not create portal credentials
     * @param {string} clientData.firstName
     * @param {string} clientData.lastName
     * @param {'maternity'|'lactation'|'well-woman'} clientData.clientType
     * @param {string|null} [clientData.dateOfBirth] ISO date/datetime, optional on create
     * @param {string|null} [clientData.phone]
     * @param {string} [clientData.consultLeadReferenceId]
     */
    addClient: async (midwifeId, clientData) => {
        const payload = {
            ...clientData,
            clientMRN: typeof clientData?.clientMRN === 'string'
                ? clientData.clientMRN.trim()
                : clientData?.clientMRN
        };
        if (payload.dateOfBirth === '' || payload.dateOfBirth == null) {
            delete payload.dateOfBirth;
        }
        if (typeof payload.email === 'string') payload.email = payload.email.trim() || null;
        delete payload.password;
        const res = await fetch(`${config.api.url}/midwives/${midwifeId}/clients`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        return handleResponse(res);
    },

    fetchMidwifeClients: async (midwifeId) => {
        const res = await fetch(`${config.api.url}/midwives/${midwifeId}/clients`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    // Midwife API
    fetchMidwife: async (midwifeId) => {
        
        const res = await fetch(`${config.api.url}/midwives/${midwifeId}`, {
            headers: getAuthHeaders()
        });
        
        
        if (!res.ok) {
            console.error('clientApi: fetchMidwife failed:', res.status, res.statusText);
            throw new Error(`Failed to fetch midwife information: ${res.status}`);
        }
        
        const result = await handleResponse(res);
        return result;
    },

    // Client Appointments API
    fetchClientAppointments: async (clientId) => {
        
        const res = await fetch(`${config.api.url}/clients/${clientId}/appointments`, {
            headers: getAuthHeaders()
        });
        
        
        if (!res.ok) {
            console.error('clientApi: fetchClientAppointments failed:', res.status, res.statusText);
            throw new Error(`Failed to fetch client appointments: ${res.status}`);
        }
        
        const result = await handleResponse(res);
        return (result || []).map((appointment) => ({
            ...appointment,
            startTime: normalizeAppointmentUtc(appointment.startTime ?? appointment.StartTime),
            endTime: normalizeAppointmentUtc(appointment.endTime ?? appointment.EndTime),
        }));
    },

    fetchContractionTimerSessions: async (pregnancyReferenceId, limit = 5, { signal } = {}) => {
        if (!pregnancyReferenceId) throw new Error('Pregnancy reference ID is required');
        const res = await fetch(
            `${config.api.url}/pregnancies/${pregnancyReferenceId}/contraction-timer/sessions?limit=${encodeURIComponent(limit)}`,
            { headers: getAuthHeaders(), cache: 'no-store', signal }
        );
        return handleResponse(res);
    },

    createContractionTimerSession: async (pregnancyReferenceId, sessionData = {}) => {
        if (!pregnancyReferenceId) throw new Error('Pregnancy reference ID is required');
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/contraction-timer/sessions`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(sessionData)
        });
        return handleResponse(res);
    },

    recordContractionTimerEntry: async (pregnancyReferenceId, sessionReferenceId, entryData) => {
        if (!pregnancyReferenceId) throw new Error('Pregnancy reference ID is required');
        if (!sessionReferenceId) throw new Error('Timer session reference ID is required');
        const res = await fetch(
            `${config.api.url}/pregnancies/${pregnancyReferenceId}/contraction-timer/sessions/${sessionReferenceId}/entries`,
            {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(entryData)
            }
        );
        return handleResponse(res);
    },

    deleteContractionTimerEntry: async (pregnancyReferenceId, sessionReferenceId, entryReferenceId) => {
        if (!pregnancyReferenceId) throw new Error('Pregnancy reference ID is required');
        if (!sessionReferenceId) throw new Error('Timer session reference ID is required');
        if (!entryReferenceId) throw new Error('Timer entry reference ID is required');
        const res = await fetch(
            `${config.api.url}/pregnancies/${pregnancyReferenceId}/contraction-timer/sessions/${sessionReferenceId}/entries/${entryReferenceId}`,
            {
                method: 'DELETE',
                headers: getAuthHeaders()
            }
        );
        return handleNoContentResponse(res);
    },

    sendContractionTimerSessionToMidwife: async (pregnancyReferenceId, sessionReferenceId, idempotencyKey) => {
        if (!pregnancyReferenceId) throw new Error('Pregnancy reference ID is required');
        if (!sessionReferenceId) throw new Error('Timer session reference ID is required');
        if (!idempotencyKey) throw new Error('Idempotency key is required');
        const res = await fetch(
            `${config.api.url}/pregnancies/${pregnancyReferenceId}/contraction-timer/sessions/${sessionReferenceId}/send-to-midwife`,
            {
                method: 'POST',
                headers: {
                    ...getAuthHeaders(),
                    'Idempotency-Key': idempotencyKey
                }
            }
        );
        return handleResponse(res);
    },

    // Call Log APIs
    fetchCallLogs: async (referenceId) => {
        try {
            const res = await fetch(`${config.api.url}/clients/${referenceId}/call-logs`, {
                headers: getAuthHeaders()
            });
            
            if (!res.ok) {
                // For 404 or 500 errors, return empty array instead of throwing
                // This allows the app to continue working even if call logs aren't implemented yet
                if (res.status === 404 || res.status === 500) {
                    return [];
                }
                const errorText = await res.text();
                console.error('clientApi: fetchCallLogs error response:', errorText);
                throw new Error(`HTTP ${res.status}: ${errorText}`);
            }
            
            const result = await handleResponse(res);
            return result || [];
        } catch (error) {
            console.error('clientApi: fetchCallLogs error:', error);
            return [];
        }
    },

    createCallLog: async (referenceId, callLogData) => {
        // Transform the data to match backend expectations (PascalCase field names)
        const transformedData = {
            dto: {
                ClientReferenceId: referenceId,
                CallDate: callLogData.callDate,
                CallTime: formatTimeSpanForSubmit(callLogData.callTime),
                CallType: callLogData.callType,
                CallDuration: callLogData.callDuration ? parseInt(callLogData.callDuration) : null,
                CallerName: callLogData.callerName || null,
                CallerRelationship: callLogData.callerRelationship || null,
                ReasonForCall: callLogData.reasonForCall,
                CallSummary: callLogData.callSummary,
                ActionTaken: callLogData.actionTaken || null,
                FollowUpRequired: callLogData.followUpRequired || false,
                FollowUpDate: callLogData.followUpDate || null,
                FollowUpNotes: callLogData.followUpNotes || null,
                Priority: callLogData.priority || 'normal',
                Category: callLogData.category || null,
                Notes: callLogData.notes || null,
                ProviderName: callLogData.providerName || null,
                ProviderCredentials: callLogData.providerCredentials || null
            }
        };
        
        const url = `${config.api.url}/clients/${referenceId}/call-logs`;
        
        try {
            const res = await fetch(url, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(transformedData)
            });
            
            if (!res.ok) {
                const errorText = await res.text();
                console.error('clientApi: Error response body:', errorText);
                throw new Error(`HTTP ${res.status}: ${errorText}`);
            }
            
            const result = await handleResponse(res);
            return result;
        } catch (error) {
            console.error('clientApi: createCallLog error:', error);
            throw error;
        }
    },

    updateCallLog: async (referenceId, callLogId, callLogData) => {
        // Transform the data to match backend expectations (PascalCase field names)
        const transformedData = {
            dto: {
                Id: callLogId,
                ClientReferenceId: referenceId,
                CallDate: callLogData.callDate,
                CallTime: formatTimeSpanForSubmit(callLogData.callTime),
                CallType: callLogData.callType,
                CallDuration: callLogData.callDuration ? parseInt(callLogData.callDuration) : null,
                CallerName: callLogData.callerName || null,
                CallerRelationship: callLogData.callerRelationship || null,
                ReasonForCall: callLogData.reasonForCall,
                CallSummary: callLogData.callSummary,
                ActionTaken: callLogData.actionTaken || null,
                FollowUpRequired: callLogData.followUpRequired || false,
                FollowUpDate: callLogData.followUpDate || null,
                FollowUpNotes: callLogData.followUpNotes || null,
                Priority: callLogData.priority || 'normal',
                Category: callLogData.category || null,
                Notes: callLogData.notes || null,
                ProviderName: callLogData.providerName || null,
                ProviderCredentials: callLogData.providerCredentials || null
            }
        };
        
        const url = `${config.api.url}/clients/${referenceId}/call-logs/${callLogId}`;
        
        try {
            const res = await fetch(url, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(transformedData)
            });
            
            
            if (!res.ok) {
                const errorText = await res.text();
                console.error('clientApi: Error response body:', errorText);
                throw new Error(`HTTP ${res.status}: ${errorText}`);
            }
            
            const result = await handleResponse(res);
            return result;
        } catch (error) {
            console.error('clientApi: updateCallLog error:', error);
            throw error;
        }
    },

    deleteCallLog: async (referenceId, callLogId) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/call-logs/${callLogId}`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        if (!res.ok) {
            throw new Error(`Failed to delete call log. Status: ${res.status}`);
        }
        return res.status === 204 ? {} : requireClinicalSaveResult(await res.json());
    },

    voidCallLog: async (referenceId, callLogId, voidReason) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/call-logs/${callLogId}/void`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ voidReason })
        });
        return handleResponse(res);
    },

    // Well-Woman Visit APIs
    fetchWellWomanVisits: async (referenceId) => {
        const local = await readClinicalResourceList({ resourceType: 'wellWomanVisit', clientReferenceId: referenceId, parentReferenceId: referenceId });
        if (local) return local;
        try {
            const res = await fetch(`${config.api.url}/clients/${referenceId}/well-woman-visits`, {
                headers: getAuthHeaders()
            });
            
            if (!res.ok) {
                if (res.status === 404 || res.status === 500) {
                    return [];
                }
                const errorText = await res.text();
                console.error('clientApi: fetchWellWomanVisits error response:', errorText);
                throw new Error(`HTTP ${res.status}: ${errorText}`);
            }
            
            const result = await handleResponse(res);
            return result || [];
        } catch (error) {
            console.error('clientApi: fetchWellWomanVisits error:', error);
            return [];
        }
    },

    createWellWomanVisit: async (referenceId, visitData) => {
        const transformedData = {
            dto: {
                ClientReferenceId: referenceId,
                VisitDate: visitData.visitDate,
                VisitTime: formatTimeSpanForSubmit(visitData.visitTime),
                VisitType: visitData.visitType || 'well-woman',
                WellWomanVisitSubtype: visitData.wellWomanVisitSubtype || null,
                WellWomanVisitSubtypeOther: visitData.wellWomanVisitSubtypeOther || null,
                ProviderName: visitData.providerName || null,
                ProviderCredentials: visitData.providerCredentials || null,
                SupervisingProviderName: visitData.supervisingProviderName || null,
                AttendingAssistingProviderNames: visitData.attendingAssistingProviderNames || null,
                SignedTimestamp: visitData.signedTimestamp || null,
                IsRoutine: visitData.isRoutine || null,
                CptCodes: visitData.cptCodes || [],
                // Numeric vitals
                Weight: visitData.weight ? parseFloat(visitData.weight) : null,
                Height: visitData.height ? parseFloat(visitData.height) : null,
                Bmi: visitData.bmi ? parseFloat(visitData.bmi) : null,
                BpSystolic: visitData.bpSystolic ? parseInt(visitData.bpSystolic) : null,
                BpDiastolic: visitData.bpDiastolic ? parseInt(visitData.bpDiastolic) : null,
                Pulse: visitData.pulse ? parseInt(visitData.pulse) : null,
                TemperatureValue: visitData.temperatureValue ? parseFloat(visitData.temperatureValue) : null,
                RespiratoryRateValue: visitData.respiratoryRateValue ? parseInt(visitData.respiratoryRateValue) : null,
                // Normal/Abnormal pairs - Vitals & General Health
                BloodPressureNormal: visitData.bloodPressureNormal ?? null,
                BloodPressure: visitData.bloodPressure || null,
                HeartRateNormal: visitData.heartRateNormal ?? null,
                HeartRate: visitData.heartRate || null,
                RespiratoryRateNormal: visitData.respiratoryRateNormal ?? null,
                RespiratoryRate: visitData.respiratoryRate || null,
                TemperatureNormal: visitData.temperatureNormal ?? null,
                Temperature: visitData.temperature || null,
                HeightWeightBmiNormal: visitData.heightWeightBmiNormal ?? null,
                HeightWeightBmi: visitData.heightWeightBmi || null,
                GeneralAppearanceNormal: visitData.generalAppearanceNormal ?? null,
                GeneralAppearance: visitData.generalAppearance || null,
                EnergyLevelNormal: visitData.energyLevelNormal ?? null,
                EnergyLevel: visitData.energyLevel || null,
                HydrationStatusNormal: visitData.hydrationStatusNormal ?? null,
                HydrationStatus: visitData.hydrationStatus || null,
                // Breast Exam
                BreastInspectionNormal: visitData.breastInspectionNormal ?? null,
                BreastInspection: visitData.breastInspection || null,
                BreastPalpationNormal: visitData.breastPalpationNormal ?? null,
                BreastPalpation: visitData.breastPalpation || null,
                NipplesAreolaNormal: visitData.nipplesAreolaNormal ?? null,
                NipplesAreola: visitData.nipplesAreola || null,
                BreastLymphNodesNormal: visitData.breastLymphNodesNormal ?? null,
                BreastLymphNodes: visitData.breastLymphNodes || null,
                // Abdomen
                AbdomenInspectionNormal: visitData.abdomenInspectionNormal ?? null,
                AbdomenInspection: visitData.abdomenInspection || null,
                AbdomenPalpationNormal: visitData.abdomenPalpationNormal ?? null,
                AbdomenPalpation: visitData.abdomenPalpation || null,
                AbdomenTendernessNormal: visitData.abdomenTendernessNormal ?? null,
                AbdomenTenderness: visitData.abdomenTenderness || null,
                AbdomenMassesNormal: visitData.abdomenMassesNormal ?? null,
                AbdomenMasses: visitData.abdomenMasses || null,
                // Pelvic Exam
                PelvicExamDeclined: visitData.pelvicExamDeclined ?? false,
                ExternalGenitaliaNormal: visitData.externalGenitaliaNormal ?? null,
                ExternalGenitalia: visitData.externalGenitalia || null,
                VaginaNormal: visitData.vaginaNormal ?? null,
                Vagina: visitData.vagina || null,
                CervixNormal: visitData.cervixNormal ?? null,
                Cervix: visitData.cervix || null,
                BimanualExamNormal: visitData.bimanualExamNormal ?? null,
                BimanualExam: visitData.bimanualExam || null,
                // Menstrual & Reproductive Health
                LastMenstrualPeriodDate: visitData.lastMenstrualPeriodDate || null,
                CycleRegularityNormal: visitData.cycleRegularityNormal ?? null,
                CycleRegularity: visitData.cycleRegularity || null,
                MenstrualFlowNormal: visitData.menstrualFlowNormal ?? null,
                MenstrualFlow: visitData.menstrualFlow || null,
                MenstrualPainNormal: visitData.menstrualPainNormal ?? null,
                MenstrualPain: visitData.menstrualPain || null,
                PmsPmddNormal: visitData.pmsPmddNormal ?? null,
                PmsPmdd: visitData.pmsPmdd || null,
                ContraceptionFertilityNormal: visitData.contraceptionFertilityNormal ?? null,
                ContraceptionFertility: visitData.contraceptionFertility || null,
                PregnancyHistoryNormal: visitData.pregnancyHistoryNormal ?? null,
                PregnancyHistory: visitData.pregnancyHistory || null,
                // Sexual & Urogenital Health
                LibidoNormal: visitData.libidoNormal ?? null,
                Libido: visitData.libido || null,
                PainWithIntercourseNormal: visitData.painWithIntercourseNormal ?? null,
                PainWithIntercourse: visitData.painWithIntercourse || null,
                UrinarySymptomsNormal: visitData.urinarySymptomsNormal ?? null,
                UrinarySymptoms: visitData.urinarySymptoms || null,
                VaginalDrynessNormal: visitData.vaginalDrynessNormal ?? null,
                VaginalDryness: visitData.vaginalDryness || null,
                RecurrentInfectionsNormal: visitData.recurrentInfectionsNormal ?? null,
                RecurrentInfections: visitData.recurrentInfections || null,
                // Menopause / Perimenopause
                HotFlashesNormal: visitData.hotFlashesNormal ?? null,
                HotFlashes: visitData.hotFlashes || null,
                NightSweatsNormal: visitData.nightSweatsNormal ?? null,
                NightSweats: visitData.nightSweats || null,
                SleepQualityNormal: visitData.sleepQualityNormal ?? null,
                SleepQuality: visitData.sleepQuality || null,
                MoodChangesNormal: visitData.moodChangesNormal ?? null,
                MoodChanges: visitData.moodChanges || null,
                BrainFogNormal: visitData.brainFogNormal ?? null,
                BrainFog: visitData.brainFog || null,
                JointPainNormal: visitData.jointPainNormal ?? null,
                JointPain: visitData.jointPain || null,
                VaginalSymptomsNormal: visitData.vaginalSymptomsNormal ?? null,
                VaginalSymptoms: visitData.vaginalSymptoms || null,
                LibidoChangesNormal: visitData.libidoChangesNormal ?? null,
                LibidoChanges: visitData.libidoChanges || null,
                // Functional / Integrative - Systems Review
                DigestionNormal: visitData.digestionNormal ?? null,
                Digestion: visitData.digestion || null,
                EliminationNormal: visitData.eliminationNormal ?? null,
                Elimination: visitData.elimination || null,
                EnergyFatigueNormal: visitData.energyFatigueNormal ?? null,
                EnergyFatigue: visitData.energyFatigue || null,
                SleepPatternsNormal: visitData.sleepPatternsNormal ?? null,
                SleepPatterns: visitData.sleepPatterns || null,
                StressResilienceNormal: visitData.stressResilienceNormal ?? null,
                StressResilience: visitData.stressResilience || null,
                MoodNormal: visitData.moodNormal ?? null,
                Mood: visitData.mood || null,
                SkinHairNormal: visitData.skinHairNormal ?? null,
                SkinHair: visitData.skinHair || null,
                TemperatureToleranceNormal: visitData.temperatureToleranceNormal ?? null,
                TemperatureTolerance: visitData.temperatureTolerance || null,
                // Lifestyle & Foundations
                Nutrition: visitData.nutrition || null,
                Movement: visitData.movement || null,
                SleepLifestyle: visitData.sleepLifestyle || null,
                StressManagement: visitData.stressManagement || null,
                EnvironmentalExposures: visitData.environmentalExposures || null,
                SupplementsHerbs: visitData.supplementsHerbs || null,
                // Labs & Screening
                PapResultsNormal: visitData.papResultsNormal ?? null,
                PapResults: visitData.papResults || null,
                HpvScreenNormal: visitData.hpvScreenNormal ?? null,
                HpvScreen: visitData.hpvScreen || null,
                StiScreeningNormal: visitData.stiScreeningNormal ?? null,
                StiScreening: visitData.stiScreening || null,
                BreastImagingReferralNormal: visitData.breastImagingReferralNormal ?? null,
                BreastImagingReferral: visitData.breastImagingReferral || null,
                LabsOrdered: visitData.labsOrdered || null,
                // Assessment & Plan
                AssessmentSummary: visitData.assessmentSummary || null,
                PlanItems: visitData.planItems || [],
                PlanNotes: visitData.planNotes || null,
            }
        };
        
        const local = await saveClinicalResource({ resourceType: 'wellWomanVisit', clientReferenceId: referenceId, parentReferenceId: referenceId, operation: 'create', payload: { ...transformedData.dto, clientRequestId: visitData.clientRequestId } });
        if (local) return local;

        const url = `${config.api.url}/clients/${referenceId}/well-woman-visits`;
        
        try {
            const res = await fetch(url, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(transformedData)
            });
            
            if (!res.ok) {
                const errorText = await res.text();
                console.error('clientApi: createWellWomanVisit error:', errorText);
                throw new Error(`HTTP ${res.status}: ${errorText}`);
            }
            
            return requireClinicalSaveResult(await handleResponse(res));
        } catch (error) {
            console.error('clientApi: createWellWomanVisit error:', error);
            throw error;
        }
    },

    updateWellWomanVisit: async (referenceId, visitId, visitData) => {
        // Same transformation as createWellWomanVisit, with Id added
        const transformedData = {
            dto: {
                Id: visitId,
                ClientReferenceId: referenceId,
                UpdatedAt: visitData.updatedAt || visitData.UpdatedAt || visitData.createdAt || visitData.CreatedAt || null,
                VisitDate: visitData.visitDate,
                VisitTime: formatTimeSpanForSubmit(visitData.visitTime),
                VisitType: visitData.visitType || 'well-woman',
                WellWomanVisitSubtype: visitData.wellWomanVisitSubtype || null,
                WellWomanVisitSubtypeOther: visitData.wellWomanVisitSubtypeOther || null,
                ProviderName: visitData.providerName || null,
                ProviderCredentials: visitData.providerCredentials || null,
                SupervisingProviderName: visitData.supervisingProviderName || null,
                AttendingAssistingProviderNames: visitData.attendingAssistingProviderNames || null,
                SignedTimestamp: visitData.signedTimestamp || null,
                IsRoutine: visitData.isRoutine || null,
                CptCodes: visitData.cptCodes || [],
                Weight: visitData.weight ? parseFloat(visitData.weight) : null,
                Height: visitData.height ? parseFloat(visitData.height) : null,
                Bmi: visitData.bmi ? parseFloat(visitData.bmi) : null,
                BpSystolic: visitData.bpSystolic ? parseInt(visitData.bpSystolic) : null,
                BpDiastolic: visitData.bpDiastolic ? parseInt(visitData.bpDiastolic) : null,
                Pulse: visitData.pulse ? parseInt(visitData.pulse) : null,
                TemperatureValue: visitData.temperatureValue ? parseFloat(visitData.temperatureValue) : null,
                RespiratoryRateValue: visitData.respiratoryRateValue ? parseInt(visitData.respiratoryRateValue) : null,
                BloodPressureNormal: visitData.bloodPressureNormal ?? null, BloodPressure: visitData.bloodPressure || null,
                HeartRateNormal: visitData.heartRateNormal ?? null, HeartRate: visitData.heartRate || null,
                RespiratoryRateNormal: visitData.respiratoryRateNormal ?? null, RespiratoryRate: visitData.respiratoryRate || null,
                TemperatureNormal: visitData.temperatureNormal ?? null, Temperature: visitData.temperature || null,
                HeightWeightBmiNormal: visitData.heightWeightBmiNormal ?? null, HeightWeightBmi: visitData.heightWeightBmi || null,
                GeneralAppearanceNormal: visitData.generalAppearanceNormal ?? null, GeneralAppearance: visitData.generalAppearance || null,
                EnergyLevelNormal: visitData.energyLevelNormal ?? null, EnergyLevel: visitData.energyLevel || null,
                HydrationStatusNormal: visitData.hydrationStatusNormal ?? null, HydrationStatus: visitData.hydrationStatus || null,
                BreastInspectionNormal: visitData.breastInspectionNormal ?? null, BreastInspection: visitData.breastInspection || null,
                BreastPalpationNormal: visitData.breastPalpationNormal ?? null, BreastPalpation: visitData.breastPalpation || null,
                NipplesAreolaNormal: visitData.nipplesAreolaNormal ?? null, NipplesAreola: visitData.nipplesAreola || null,
                BreastLymphNodesNormal: visitData.breastLymphNodesNormal ?? null, BreastLymphNodes: visitData.breastLymphNodes || null,
                AbdomenInspectionNormal: visitData.abdomenInspectionNormal ?? null, AbdomenInspection: visitData.abdomenInspection || null,
                AbdomenPalpationNormal: visitData.abdomenPalpationNormal ?? null, AbdomenPalpation: visitData.abdomenPalpation || null,
                AbdomenTendernessNormal: visitData.abdomenTendernessNormal ?? null, AbdomenTenderness: visitData.abdomenTenderness || null,
                AbdomenMassesNormal: visitData.abdomenMassesNormal ?? null, AbdomenMasses: visitData.abdomenMasses || null,
                PelvicExamDeclined: visitData.pelvicExamDeclined ?? false,
                ExternalGenitaliaNormal: visitData.externalGenitaliaNormal ?? null, ExternalGenitalia: visitData.externalGenitalia || null,
                VaginaNormal: visitData.vaginaNormal ?? null, Vagina: visitData.vagina || null,
                CervixNormal: visitData.cervixNormal ?? null, Cervix: visitData.cervix || null,
                BimanualExamNormal: visitData.bimanualExamNormal ?? null, BimanualExam: visitData.bimanualExam || null,
                LastMenstrualPeriodDate: visitData.lastMenstrualPeriodDate || null,
                CycleRegularityNormal: visitData.cycleRegularityNormal ?? null, CycleRegularity: visitData.cycleRegularity || null,
                MenstrualFlowNormal: visitData.menstrualFlowNormal ?? null, MenstrualFlow: visitData.menstrualFlow || null,
                MenstrualPainNormal: visitData.menstrualPainNormal ?? null, MenstrualPain: visitData.menstrualPain || null,
                PmsPmddNormal: visitData.pmsPmddNormal ?? null, PmsPmdd: visitData.pmsPmdd || null,
                ContraceptionFertilityNormal: visitData.contraceptionFertilityNormal ?? null, ContraceptionFertility: visitData.contraceptionFertility || null,
                PregnancyHistoryNormal: visitData.pregnancyHistoryNormal ?? null, PregnancyHistory: visitData.pregnancyHistory || null,
                LibidoNormal: visitData.libidoNormal ?? null, Libido: visitData.libido || null,
                PainWithIntercourseNormal: visitData.painWithIntercourseNormal ?? null, PainWithIntercourse: visitData.painWithIntercourse || null,
                UrinarySymptomsNormal: visitData.urinarySymptomsNormal ?? null, UrinarySymptoms: visitData.urinarySymptoms || null,
                VaginalDrynessNormal: visitData.vaginalDrynessNormal ?? null, VaginalDryness: visitData.vaginalDryness || null,
                RecurrentInfectionsNormal: visitData.recurrentInfectionsNormal ?? null, RecurrentInfections: visitData.recurrentInfections || null,
                HotFlashesNormal: visitData.hotFlashesNormal ?? null, HotFlashes: visitData.hotFlashes || null,
                NightSweatsNormal: visitData.nightSweatsNormal ?? null, NightSweats: visitData.nightSweats || null,
                SleepQualityNormal: visitData.sleepQualityNormal ?? null, SleepQuality: visitData.sleepQuality || null,
                MoodChangesNormal: visitData.moodChangesNormal ?? null, MoodChanges: visitData.moodChanges || null,
                BrainFogNormal: visitData.brainFogNormal ?? null, BrainFog: visitData.brainFog || null,
                JointPainNormal: visitData.jointPainNormal ?? null, JointPain: visitData.jointPain || null,
                VaginalSymptomsNormal: visitData.vaginalSymptomsNormal ?? null, VaginalSymptoms: visitData.vaginalSymptoms || null,
                LibidoChangesNormal: visitData.libidoChangesNormal ?? null, LibidoChanges: visitData.libidoChanges || null,
                DigestionNormal: visitData.digestionNormal ?? null, Digestion: visitData.digestion || null,
                EliminationNormal: visitData.eliminationNormal ?? null, Elimination: visitData.elimination || null,
                EnergyFatigueNormal: visitData.energyFatigueNormal ?? null, EnergyFatigue: visitData.energyFatigue || null,
                SleepPatternsNormal: visitData.sleepPatternsNormal ?? null, SleepPatterns: visitData.sleepPatterns || null,
                StressResilienceNormal: visitData.stressResilienceNormal ?? null, StressResilience: visitData.stressResilience || null,
                MoodNormal: visitData.moodNormal ?? null, Mood: visitData.mood || null,
                SkinHairNormal: visitData.skinHairNormal ?? null, SkinHair: visitData.skinHair || null,
                TemperatureToleranceNormal: visitData.temperatureToleranceNormal ?? null, TemperatureTolerance: visitData.temperatureTolerance || null,
                Nutrition: visitData.nutrition || null, Movement: visitData.movement || null,
                SleepLifestyle: visitData.sleepLifestyle || null, StressManagement: visitData.stressManagement || null,
                EnvironmentalExposures: visitData.environmentalExposures || null, SupplementsHerbs: visitData.supplementsHerbs || null,
                PapResultsNormal: visitData.papResultsNormal ?? null, PapResults: visitData.papResults || null,
                HpvScreenNormal: visitData.hpvScreenNormal ?? null, HpvScreen: visitData.hpvScreen || null,
                StiScreeningNormal: visitData.stiScreeningNormal ?? null, StiScreening: visitData.stiScreening || null,
                BreastImagingReferralNormal: visitData.breastImagingReferralNormal ?? null, BreastImagingReferral: visitData.breastImagingReferral || null,
                LabsOrdered: visitData.labsOrdered || null,
                AssessmentSummary: visitData.assessmentSummary || null,
                PlanItems: visitData.planItems || [],
                PlanNotes: visitData.planNotes || null,
            }
        };
        
        const local = await saveClinicalResource({ resourceType: 'wellWomanVisit', clientReferenceId: referenceId, parentReferenceId: referenceId, resourceReferenceId: visitId, operation: 'update', payload: { ...transformedData.dto, clientRequestId: visitData.clientRequestId } });
        if (local) return local;

        visitId = await clinicalNetworkRouteId({ resourceType: 'wellWomanVisit', clientReferenceId: referenceId }, visitId);
        const url = `${config.api.url}/clients/${referenceId}/well-woman-visits/${visitId}`;
        
        try {
            const res = await fetch(url, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(transformedData)
            });
            
            if (!res.ok) {
                const errorText = await res.text();
                console.error('clientApi: updateWellWomanVisit error:', errorText);
                throw new Error(`HTTP ${res.status}: ${errorText}`);
            }
            
            return requireClinicalSaveResult(await handleResponse(res));
        } catch (error) {
            console.error('clientApi: updateWellWomanVisit error:', error);
            throw error;
        }
    },

    deleteWellWomanVisit: async (referenceId, visitId) => {
        const local = await saveClinicalResource({ resourceType: 'wellWomanVisit', clientReferenceId: referenceId, parentReferenceId: referenceId, resourceReferenceId: visitId, operation: 'delete' });
        if (local) return local;
        visitId = await clinicalNetworkRouteId({ resourceType: 'wellWomanVisit', clientReferenceId: referenceId }, visitId);
        const res = await fetch(`${config.api.url}/clients/${referenceId}/well-woman-visits/${visitId}`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        if (!res.ok) {
            throw new Error(`Failed to delete well-woman visit. Status: ${res.status}`);
        }
        return res.status === 204 ? {} : requireClinicalSaveResult(await res.json());
    },

    voidWellWomanVisit: async (referenceId, visitId, voidReason) => {
        await assertClinicalChartReady({ resourceType: 'wellWomanVisit', clientReferenceId: referenceId, resourceReferenceId: visitId });
        visitId = await clinicalNetworkRouteId({ resourceType: 'wellWomanVisit', clientReferenceId: referenceId }, visitId);
        const res = await fetch(`${config.api.url}/clients/${referenceId}/well-woman-visits/${visitId}/void`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ voidReason })
        });
        return handleResponse(res);
    },

    // My Midwife API
    fetchMyMidwife: async () => {
        const headers = getAuthHeaders();
        
        const res = await fetch(`${config.api.url}/clients/my-midwife`, {
            headers: headers
        });
        
        if (!res.ok) {
            const errorText = await res.text();
            throw new Error(`Failed to fetch midwife information: ${res.status} - ${errorText}`);
        }
        
        const result = await handleResponse(res);
        return result;
    },

    // Midwife Profile APIs
    fetchMidwifeProfile: async () => {
        const res = await fetch(`${config.api.url}/midwife-profile`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    updateMidwifeProfile: async (profileData) => {
        const res = await fetch(`${config.api.url}/midwife-profile`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(profileData)
        });
        return handleResponse(res);
    },

    fetchCollaboratingPhysician: async (prescriberReferenceId) => {
        const res = await fetch(`${config.api.url}/midwife-profile/${prescriberReferenceId}/collaborating-physician`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    updateCollaboratingPhysician: async (prescriberReferenceId, physician) => {
        const res = await fetch(`${config.api.url}/midwife-profile/${prescriberReferenceId}/collaborating-physician`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(physician)
        });
        return handleResponse(res);
    },

    // Midwife License APIs
    fetchLicenses: async () => {
        const res = await fetch(`${config.api.url}/midwife-profile/licenses`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    createLicense: async (licenseData) => {
        const res = await fetch(`${config.api.url}/midwife-profile/licenses`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(licenseData)
        });
        return handleResponse(res);
    },

    updateLicense: async (id, licenseData) => {
        const res = await fetch(`${config.api.url}/midwife-profile/licenses/${id}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(licenseData)
        });
        return handleNoContentResponse(res);
    },

    deleteLicense: async (id) => {
        const res = await fetch(`${config.api.url}/midwife-profile/licenses/${id}`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    // Organization Profile APIs
    fetchOrganizationProfile: async () => {
        const res = await fetch(`${config.api.url}/midwife-profile/organization`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    updateOrganizationProfile: async (profileData) => {
        const res = await fetch(`${config.api.url}/midwife-profile/organization`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(profileData)
        });
        return handleResponse(res);
    },

    // Baby APIs (existing functionality)
    fetchBabies: async (clientReferenceId, pregnancyReferenceId) => {
        const res = await fetch(`${config.api.url}/clients/${clientReferenceId}/pregnancies/${pregnancyReferenceId}/babies`, {
            headers: getAuthHeaders()
        });
        
        const result = await handleResponse(res);
        
        return result;
    },

    createBaby: async (clientReferenceId, pregnancyReferenceId, babyData) => {
        const res = await fetch(`${config.api.url}/clients/${clientReferenceId}/pregnancies/${pregnancyReferenceId}/babies`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(babyData)
        });
        return handleResponse(res);
    },

    updateBaby: async (clientReferenceId, pregnancyReferenceId, babyId, babyData) => {
        const res = await fetch(`${config.api.url}/clients/${clientReferenceId}/pregnancies/${pregnancyReferenceId}/babies/${babyId}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(babyData)
        });
        return handleResponse(res);
    },

    // Client chart profile image (preferred) with legacy user upload fallback on 404.
    uploadClientProfileImage: async (clientReferenceId, imageFile, options = {}) => {
        if (!clientReferenceId) throw new Error('Client reference ID is required');

        const { userReferenceId } = options;
        let res = await postProfileImageMultipart(
            `${config.api.url}/clients/${clientReferenceId}/profile-image`,
            imageFile
        );

        if (res.status === 404 && userReferenceId) {
            res = await postProfileImageMultipart(
                `${config.api.url}/User/${userReferenceId}/profile-image`,
                imageFile
            );
        }

        if (res.status === 404) {
            throw new Error('Photo upload is not available on this server yet.');
        }

        if (!res.ok) {
            throw new Error(profileImageUploadErrorMessage(res.status));
        }

        return parseProfileImageUploadResponse(res);
    },

    // User Profile Image APIs (legacy; still used when chart endpoint is unavailable)
    uploadProfileImage: async (userReferenceId, imageFile) => {
        if (!userReferenceId) throw new Error('User reference ID is required');

        const res = await postProfileImageMultipart(
            `${config.api.url}/User/${userReferenceId}/profile-image`,
            imageFile
        );

        if (!res.ok) {
            throw new Error(profileImageUploadErrorMessage(res.status));
        }

        return parseProfileImageUploadResponse(res);
    },

    fetchUser: async (userReferenceId) => {
        if (!userReferenceId) throw new Error('User reference ID is required');
        
        try {
            const res = await fetch(`${config.api.url}/User/${userReferenceId}`, {
                headers: getAuthHeaders()
            });
            
            if (!res.ok) {
                const errorText = await res.text();
                console.error('Fetch user failed:', {
                    status: res.status,
                    statusText: res.statusText,
                    error: errorText
                });
                throw new Error(errorText || `Failed to fetch user. Status: ${res.status}`);
            }
            
            return handleResponse(res);
        } catch (err) {
            console.error('Error in fetchUser:', err);
            throw err;
        }
    },


};

export default clientApi;
